<template>
  <el-dialog
    :visible="true"
    :show-close="false"
    width="820px"
    custom-class="link-invoice-modal"
    @close="$emit('close')"
  >
    <!-- Header -->
    <div slot="title" class="li-header">
      <span class="li-header__title"
        >Vincular facturas — {{ projectName }}</span
      >
      <button class="li-header__close" @click="$emit('close')">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <div class="li-body">
      <!-- Buscador de cliente -->
      <div class="li-search">
        <div class="li-search__label">Filtrar por cliente</div>
        <el-select
          v-model="selectedClientId"
          placeholder="Todos los clientes"
          clearable
          filterable
          remote
          :remote-method="searchClients"
          :loading="loadingClients"
          style="width: 100%"
          size="small"
          @change="onClientChange"
        >
          <el-option
            v-for="c in clients"
            :key="c.id"
            :label="c.name"
            :value="c.id"
          />
        </el-select>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="li-loading">
        <i class="el-icon-loading"></i> Cargando facturas...
      </div>

      <!-- Empty -->
      <div v-else-if="displayedInvoices.length === 0" class="li-empty">
        <span v-if="selectedClientId">
          Este cliente no tiene facturas disponibles.
        </span>
        <span v-else>No hay facturas disponibles para vincular.</span>
      </div>

      <!-- Tabla -->
      <template v-else>
        <div class="li-count">
          {{ displayedInvoices.length }} factura{{
            displayedInvoices.length !== 1 ? "s" : ""
          }}
          disponible{{ displayedInvoices.length !== 1 ? "s" : "" }}
          <span v-if="loadingMore"> · cargando más...</span>
        </div>
        <div class="li-table" @scroll="onTableScroll">
          <div class="li-table__header">
            <span class="li-table__check-col">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleAll"
              />
            </span>
            <span>Factura</span>
            <span>Cliente</span>
            <span>Total</span>
            <span>Fecha</span>
            <span>Estado</span>
          </div>
          <div
            class="li-table__row"
            v-for="inv in displayedInvoices"
            :key="inv.id"
            @click="toggleRow(inv)"
          >
            <span class="li-table__check-col">
              <input
                type="checkbox"
                :checked="isSelected(inv)"
                @click.stop="toggleRow(inv)"
              />
            </span>
            <span class="li-inv-link" @click.stop="openPreview(inv)">{{
              inv.number_invoice
            }}</span>
            <span>{{ inv.client ? inv.client.name : "—" }}</span>
            <span class="li-amount">${{ formatNum(inv.total) }}</span>
            <span>{{ inv.emission_date }}</span>
            <span>
              <span :class="['li-status', statusClass(inv.status)]">{{
                statusLabel(inv.status)
              }}</span>
            </span>
          </div>
          <div v-if="loadingMore" class="li-loading-more">
            <i class="el-icon-loading"></i> Cargando más...
          </div>
        </div>
      </template>
    </div>

    <!-- Footer -->
    <div slot="footer" class="li-footer">
      <button class="li-btn li-btn--cancel" @click="$emit('close')">
        Cancelar
      </button>
      <button
        class="li-btn li-btn--primary"
        :disabled="selected.length === 0 || linking"
        @click="linkSelected"
      >
        <span v-if="linking"><i class="el-icon-loading"></i></span>
        <span v-else>
          Vincular{{ selected.length > 0 ? ` (${selected.length})` : "" }}
        </span>
      </button>
    </div>

    <!-- Preview de factura -->
    <InvoicePreview
      v-if="previewInvoice"
      :invoice="previewInvoice"
      :showModal="showPreview"
      :status="previewInvoice.status"
      @close="closePreview"
      @action="handlePreviewAction"
    />
  </el-dialog>
</template>

<script>
import projectService from "../services/projectService";
import invoiceService from "../../invoices/services/invoiceService";
import businessService from "../../../../business/services/businessService";
import Api from "@/config/Api";
import InvoicePreview from "../../invoices/components/InvoicePreview";

export default {
  name: "LinkInvoiceModal",
  components: { InvoicePreview },
  props: {
    projectId: { type: Number, required: true },
    projectName: { type: String, default: "" },
    linkedIds: { type: Array, default: () => [] }
  },
  data() {
    return {
      allInvoices: [],
      clients: [],
      selectedClientId: null,
      selected: [],
      loading: false,
      loadingMore: false,
      loadingClients: false,
      linking: false,
      page: 1,
      hasMore: true,
      previewInvoice: null,
      showPreview: false
    };
  },
  computed: {
    displayedInvoices() {
      return this.allInvoices.filter(
        inv =>
          !this.linkedIds.includes(inv.id) &&
          inv.status !== "nuled" &&
          inv.status !== "failed"
      );
    },
    allSelected() {
      return (
        this.displayedInvoices.length > 0 &&
        this.displayedInvoices.every(inv => this.isSelected(inv))
      );
    }
  },
  mounted() {
    this.loadInvoices(true);
    this.loadClients();
  },
  methods: {
    async loadInvoices(reset = false) {
      if (reset) {
        this.page = 1;
        this.hasMore = true;
        this.allInvoices = [];
      }
      if (!this.hasMore) return;

      if (this.page === 1) this.loading = true;
      else this.loadingMore = true;

      try {
        let res;
        if (this.selectedClientId) {
          // Same pattern as DocumentsTab.vue — server-side filter by client
          res = await invoiceService.getClientInvoices(this.selectedClientId, {
            params: { per_page: 200, page: this.page }
          });
        } else {
          res = await invoiceService.getInvoices({
            params: { per_page: 50, page: this.page }
          });
        }
        const batch = res.data.data || [];
        const pageSize = this.selectedClientId ? 200 : 50;
        this.allInvoices = reset ? batch : [...this.allInvoices, ...batch];
        this.hasMore = batch.length === pageSize;
        if (this.hasMore) this.page++;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    onTableScroll(e) {
      const el = e.target;
      if (
        !this.loadingMore &&
        this.hasMore &&
        el.scrollHeight - el.scrollTop - el.clientHeight < 80
      ) {
        this.loadInvoices();
      }
    },
    async loadClients(query) {
      this.loadingClients = true;
      try {
        const params = { per_page: 50 };
        if (query) params.name = query;
        const res = await businessService.getBusiness({ params });
        if (res.data && res.data.clients) {
          this.clients = res.data.clients;
        }
      } catch (e) {
        /* silent */
      } finally {
        this.loadingClients = false;
      }
    },
    searchClients(query) {
      if (query && query.length >= 2) {
        this.loadClients(query);
      } else if (!query) {
        this.loadClients();
      }
    },
    onClientChange() {
      this.selected = [];
      this.loadInvoices(true);
    },
    isSelected(inv) {
      return this.selected.some(s => s.id === inv.id);
    },
    toggleRow(inv) {
      if (this.isSelected(inv)) {
        this.selected = this.selected.filter(s => s.id !== inv.id);
      } else {
        this.selected = [...this.selected, inv];
      }
    },
    toggleAll(e) {
      if (e.target.checked) {
        const toAdd = this.displayedInvoices.filter(
          inv => !this.isSelected(inv)
        );
        this.selected = [...this.selected, ...toAdd];
      } else {
        const ids = this.displayedInvoices.map(inv => inv.id);
        this.selected = this.selected.filter(s => !ids.includes(s.id));
      }
    },
    async openPreview(invoice) {
      try {
        const res = await Api().get(`/invoices/${invoice.id}`);
        const full = res.data.data || res.data;
        this.previewInvoice = full;
      } catch (e) {
        this.previewInvoice = invoice;
      }
      this.showPreview = true;
    },
    closePreview() {
      this.showPreview = false;
      this.previewInvoice = null;
    },
    handlePreviewAction() {
      // Solo vista previa, no acciones
    },
    async linkSelected() {
      this.linking = true;
      try {
        for (const inv of this.selected) {
          await projectService.attachInvoice(this.projectId, inv.id);
        }
        this.$notifications.success({
          message: `${this.selected.length} factura(s) vinculada(s).`
        });
        this.$emit("linked");
      } finally {
        this.linking = false;
      }
    },
    formatNum(val) {
      if (!val) return "0.00";
      return Number(val).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    statusLabel(s) {
      const map = {
        draft: "Borrador",
        success: "Aprobada",
        failed: "Fallida"
      };
      return map[s] || s;
    },
    statusClass(s) {
      return "li-status--" + (s || "draft");
    }
  }
};
</script>

<style lang="scss">
.link-invoice-modal {
  border-radius: 12px !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    border-bottom: 1px solid #eef0f8;
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #eef0f8;
  }

  @media (max-width: 768px) {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100vh !important;
    max-height: 100vh !important;
    margin: 0 !important;
    border-radius: 0 !important;

    .el-dialog__body {
      height: calc(100vh - 108px);
      overflow-y: hidden;
    }
  }
}
</style>

<style lang="scss" scoped>
/* ── Header ── */
.li-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #2d2b55;
  }

  &__close {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    color: #c0c4cc;
    padding: 2px;
    border-radius: 4px;
    transition: all 0.15s;

    &:hover {
      color: #2d2b55;
      background: #f5f5f5;
    }
  }
}

/* ── Body ── */
.li-body {
  padding: 20px;
}

/* ── Search ── */
.li-search {
  margin-bottom: 16px;

  &__label {
    font-size: 11px;
    color: #9ca3af;
    margin-bottom: 5px;
    font-weight: 500;
  }
}

/* ── Scroll container ── */
.li-scroll {
  max-height: 360px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.li-loading-more {
  text-align: center;
  padding: 12px;
  color: #c0c4cc;
  font-size: 12px;
}

/* ── Count ── */
.li-count {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 8px;
}

/* ── Invoice link ── */
.li-inv-link {
  color: #2d2b55;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: all 0.15s;

  &:hover {
    color: #3fdbbf;
    text-decoration-color: #3fdbbf;
  }
}

/* ── Amount ── */
.li-amount {
  font-weight: 600;
  color: #1f2937;
}

/* ── Status badges ── */
.li-status {
  display: inline-flex;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 500;
  white-space: nowrap;

  &--draft {
    background: #f1efe8;
    color: #5f5e5a;
  }

  &--success {
    background: #e1f5ee;
    color: #0f6e56;
  }

  &--failed {
    background: #fcebeb;
    color: #a32d2d;
  }
}

/* ── Custom Table ── */
.li-table {
  border: 1px solid #eef0f8;
  border-radius: 10px;
  overflow: hidden;
  max-height: 360px;
  overflow-y: auto;

  &__header,
  &__row {
    display: grid;
    grid-template-columns: 36px 160px 1fr 110px 100px 110px;
    align-items: center;
  }

  &__check-col {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;

    input[type="checkbox"] {
      cursor: pointer;
      width: 14px;
      height: 14px;
      accent-color: #3ecfb4;
    }
  }

  &__header {
    background: #f7f8fa;
    border-bottom: 1px solid #eef0f8;
    position: sticky;
    top: 0;
    z-index: 1;

    span:not(.li-table__check-col) {
      padding: 9px 10px;
      font-size: 11px;
      font-weight: 600;
      color: #9a9ab0;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
  }

  &__row {
    border-bottom: 1px solid #f0f0f5;
    cursor: pointer;
    transition: background 0.12s;

    &:last-child {
      border-bottom: none;
    }
    &:hover {
      background: #f9f9fc;
    }

    span:not(.li-table__check-col):not(.li-inv-link):not(.li-amount):not(.li-status) {
      padding: 9px 10px;
      font-size: 13px;
      color: #4b5563;
    }

    .li-inv-link {
      padding: 9px 10px;
      display: block;
    }

    .li-amount {
      padding: 9px 10px;
      display: block;
    }

    .li-status {
      margin: 0 10px;
    }
  }
}

/* ── Loading / Empty ── */
.li-loading,
.li-empty {
  text-align: center;
  padding: 40px 20px;
  color: #c0c4cc;
  font-size: 13px;
}

/* ── Footer ── */
.li-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
}

.li-btn {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: none;

  &--cancel {
    background: transparent;
    border: 1px solid #e4eaf6;
    color: #606266;

    &:hover {
      background: #f5f5f5;
    }
  }

  &--primary {
    background: #3fdbbf;
    color: #fff;
    font-weight: 600;

    &:hover {
      background: #0e8c87;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

@media (max-width: 768px) {
  .li-body {
    padding: 14px;
  }

  .li-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &__header,
    &__row {
      /* Replace 1fr with fixed width so the grid doesn't collapse */
      grid-template-columns: 36px 160px 150px 110px 100px 110px;
    }
  }
}
</style>
